import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getLead, createLead, updateLead } from '../services/firestore';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

import { ArrowLeft, Save, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';

export const LeadFormPage = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();

  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    contact_name: '',
    company_name: '',
    phone: '',
    email: '',
    address: '',
    source: '',
    status: 'new',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  /* =========================
     OPTIONS
  ========================= */

  const sourceOptions = [
    'Website',
    'Referral',
    'Walk-in',
    'Phone Call',
    'Social Media',
    'Advertisement',
    'Trade Show',
    'Other'
  ];

  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' }
  ];

  /* =========================
     FETCH (EDIT MODE)
  ========================= */

  useEffect(() => {
    if (isEdit) fetchLead();
  }, [id]);

  const fetchLead = async () => {
    try {

      const lead = await getLead(id);

      if (!lead) {
        toast.error('Lead not found');
        navigate('/leads');
        return;
      }

      setFormData({
        contact_name: lead.contact_name || '',
        company_name: lead.company_name || '',
        phone: lead.phone || '',
        email: lead.email || '',
        address: lead.address || '',
        source: lead.source || '',
        status: lead.status || 'new',
        notes: lead.notes || ''
      });

    } catch (error) {

      console.error(error);
      toast.error('Failed to load lead');
      navigate('/leads');

    } finally {

      setLoading(false);

    }
  };

  /* =========================
     PREFILL (OCR FLOW)
  ========================= */

  useEffect(() => {

    if (!isEdit && location.state?.prefill) {

      const data = location.state.prefill;

      setFormData(prev => ({
        ...prev,
        contact_name: data.contact_name || '',
        company_name: data.company_name || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        notes: data.notes || ''
      }));

      // clear state after applying
      navigate(location.pathname, { replace: true });

    }

  }, [location.state, isEdit]);

  /* =========================
     VALIDATION
  ========================= */

  const validateForm = () => {

    const newErrors = {};

    if (!formData.contact_name.trim()) {
      newErrors.contact_name = 'Contact name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* =========================
     INPUT HANDLERS
  ========================= */

  const handleChange = (e) => {

    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /* =========================
     SUBMIT
  ========================= */

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setSaving(true);

    try {

      if (isEdit) {

        await updateLead(id, formData);
        toast.success('Lead updated successfully');

      } else {

        const result = await createLead(formData, userData.uid);
        toast.success(`Lead ${result.lead_no} created successfully`);

      }

      navigate('/leads');

    } catch (error) {

      console.error(error);
      toast.error(isEdit ? 'Failed to update lead' : 'Failed to create lead');

    } finally {

      setSaving(false);

    }
  };

  /* =========================
     LOADING
  ========================= */

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  /* =========================
     UI
  ========================= */

  return (

    <div className="p-4 md:p-6 max-w-2xl mx-auto">

      {/* HEADER */}
      <div className="flex items-center gap-4 mb-6">

        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/leads')}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? 'Edit Lead' : 'New Lead'}
          </h1>
          <p className="text-slate-500 text-sm">
            {isEdit ? 'Update lead details' : 'Add a new sales lead'}
          </p>
        </div>

      </div>

      {/* FORM */}
      <form onSubmit={handleSubmit}>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-blue-500" />
              Lead Information
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">

            {/* CONTACT NAME */}
            <div>
              <Label>Contact Name *</Label>
              <Input
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
              />
            </div>

            {/* COMPANY */}
            <div>
              <Label>Company Name</Label>
              <Input
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
              />
            </div>

            {/* PHONE + EMAIL */}
            <div className="grid grid-cols-2 gap-4">

              <div>
                <Label>Phone</Label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

            </div>

            {/* ADDRESS */}
            <div>
              <Label>Address</Label>
              <Textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={2}
              />
            </div>

            {/* SOURCE + STATUS */}
            <div className="grid grid-cols-2 gap-4">

              <div>
                <Label>Source</Label>
                <Select
                  value={formData.source}
                  onValueChange={(value) => handleSelectChange('source', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceOptions.map(source => (
                      <SelectItem key={source} value={source}>{source}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleSelectChange('status', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            </div>

            {/* NOTES */}
            <div>
              <Label>Notes</Label>
              <Textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
              />
            </div>

          </CardContent>
        </Card>

        {/* ACTIONS */}
        <div className="flex gap-3 mt-6">

          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => navigate('/leads')}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            className="flex-1 solar-gradient"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? 'Update' : 'Create'} Lead
              </>
            )}
          </Button>

        </div>

      </form>

    </div>
  );
};

export default LeadFormPage;